import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { AttendanceService } from '../../attendance.service';
import { EmployeeAttendanceDetails, TourLog } from '../../attendance.model';
import { getDefaultProfilePicUrl, handleImageError } from '../../../utils';

@Component({
  selector: 'app-attendance-detail',
  standalone: false,
  templateUrl: './attendance-detail.component.html',
  styleUrl: './attendance-detail.component.scss',
})
export class AttendanceDetailComponent implements OnInit {
  @ViewChild('imagePreviewModal') imagePreviewModal!: TemplateRef<any>;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  previewImageUrl: string = '';
  private _modalRef: NgbModalRef | null = null;
  employeeId!: string;
  employeeName: string = '';
  employeeStatus: string = '';

  isLoading = true;
  attendanceDetails: EmployeeAttendanceDetails | null = null;
  logs: TourLog[] = [];
  today: string = '';

  // Google Maps
  zoom = 15;
  mapCenter: google.maps.LatLngLiteral = { lat: 20.5937, lng: 78.9629 };
  checkInMarkers: { position: google.maps.LatLngLiteral; label: string; time: string; address: string }[] = [];
  checkOutMarkers: { position: google.maps.LatLngLiteral; label: string; time: string; address: string }[] = [];
  polylinePaths: google.maps.LatLngLiteral[][] = [];
  travelPaths: google.maps.LatLngLiteral[][] = [];
  hasMapData = false;
  activeMarkerInfo: { label: string; time: string; address: string; type: 'check-in' | 'check-out' } | null = null;

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;
  protected readonly handleImageError = handleImageError;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _attendanceService: AttendanceService,
    private _modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.employeeId = this._route.snapshot.paramMap.get('id')!;

    const navState = history.state as { name?: string; status?: string };
    this.employeeName = navState?.name ?? '';
    this.employeeStatus = navState?.status ?? '';

    const now = new Date();
    this.today = now.toISOString().split('T')[0];

    this._attendanceService
      .employee_attendance(this.employeeId, { start_date: this.today, end_date: this.today })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result: EmployeeAttendanceDetails[]) => {
          if (result?.length) {
            this.attendanceDetails = result[0];
            this.logs = result[0].logs ?? [];
          } else {
            this.logs = [];
          }
          this._buildMapData();
        },
      });
  }

  private _buildMapData(): void {
    this.checkInMarkers = [];
    this.checkOutMarkers = [];
    this.polylinePaths = [];
    this.travelPaths = [];

    this.logs.forEach((log, i) => {
      if (log.check_in_location?.latitude) {
        const ciPos: google.maps.LatLngLiteral = {
          lat: log.check_in_location.latitude,
          lng: log.check_in_location.longitude,
        };
        this.checkInMarkers.push({
          position: ciPos,
          label: `S${i + 1}`,
          time: log.check_in,
          address: log.check_in_location.address ?? '',
        });

        if (i === 0) {
          this.mapCenter = ciPos;
        }

        if (log.check_out_location?.latitude) {
          const coPos: google.maps.LatLngLiteral = {
            lat: log.check_out_location.latitude,
            lng: log.check_out_location.longitude,
          };
          this.checkOutMarkers.push({
            position: coPos,
            label: `E${i + 1}`,
            time: log.check_out ?? '',
            address: log.check_out_location.address ?? '',
          });
          this.polylinePaths.push([ciPos, coPos]);

          // Connect E(i) → S(i+1) if the next session has a check-in location
          const nextLog = this.logs[i + 1];
          if (nextLog?.check_in_location?.latitude) {
            const nextCiPos: google.maps.LatLngLiteral = {
              lat: nextLog.check_in_location.latitude,
              lng: nextLog.check_in_location.longitude,
            };
            this.travelPaths.push([coPos, nextCiPos]);
          }
        }
      }
    });

    this.hasMapData = this.checkInMarkers.length > 0;
  }

  protected onMarkerHover(
    marker: MapMarker,
    info: { label: string; time: string; address: string },
    type: 'check-in' | 'check-out'
  ): void {
    this.activeMarkerInfo = { ...info, type };
    this.infoWindow.open(marker);
  }

  protected onMarkerOut(): void {
    this.infoWindow.close();
  }

  protected get firstCheckIn(): string | null {
    return (
      this.logs
        .map(l => l.check_in)
        .filter(Boolean)
        .sort()[0] ?? null
    );
  }

  protected get lastCheckOut(): string | null {
    return (
      this.logs
        .map(l => l.check_out)
        .filter(Boolean)
        .sort()
        .pop() ?? null
    );
  }

  protected get totalOpeningKm(): number {
    if (!this.logs.length) return 0;
    return this.logs[0].opening_km ?? 0;
  }

  protected get totalClosingKm(): number | null {
    const last = [...this.logs].reverse().find(l => l.closing_km != null);
    return last?.closing_km ?? null;
  }

  protected get totalKmTravelled(): number {
    return this.logs.reduce((sum, log) => {
      const diff = (log.closing_km ?? 0) - (log.opening_km ?? 0);
      return sum + (diff > 0 ? diff : 0);
    }, 0);
  }

  protected openImagePreview(url: string): void {
    this.previewImageUrl = url;
    this._modalRef = this._modalService.open(this.imagePreviewModal, {
      size: 'lg',
      centered: true,
      backdrop: true,
    });
  }

  protected goBack(): void {
    this._router.navigate(['/attendance']);
  }
}
